import React, { useMemo, useState } from "react";
import { Card, CardBody, Table } from "reactstrap";
import { WINDOW_LABELS, WINDOW_ORDER } from "../utils/windows";
import AssetClassToggle from "./AssetClassToggle";

// Shape of the per-window / overall `diagnostics` block returned by /api/track-record
// (whitepaper §7.3/§7.4 — the "edge against the right null"). Base-rate fields are
// per-window only; the overall block carries the market-relative view but no base rate.
// `by_asset_class` stratifies the same diagnostics per equity/forex/crypto/futures, each
// scored against ITS OWN base rate (trending FX/crypto have base rates far from 50%).
export interface Diagnostics {
  win_rate: number | null;
  // CI bounds are null when a (small) sample has < 3 distinct dates — the date-clustered
  // bootstrap can't run. Common for sparse asset classes (forex/crypto/futures).
  win_rate_ci: [number | null, number | null];
  n_signals: number;
  n_dates: number;
  bull_win_rate?: number;
  bear_win_rate?: number;
  base_rate_up?: number;
  bull_edge_over_base?: number;
  bear_edge_over_base?: number;
  market_relative_win_rate?: number;
  market_relative_avg_return?: number;
  by_asset_class?: Record<string, Diagnostics>;
}

interface DiagData {
  windows: Record<string, { diagnostics?: Diagnostics }>;
  overall: { diagnostics?: Diagnostics; benchmark?: string };
}


function pct1(v?: number | null): string {
  return v === undefined || v === null ? "—" : `${v.toFixed(1)}%`;
}

function EdgeCell({ v }: { v?: number | null }) {
  if (v === undefined || v === null) return <span style={{ color: "var(--text-muted)" }}>—</span>;
  const color = v > 0.05 ? "var(--success)" : v < -0.05 ? "var(--error)" : "var(--text-muted)";
  return (
    <span style={{ color, fontWeight: Math.abs(v) >= 4 ? 700 : 500 }}>
      {v > 0 ? "+" : ""}{v.toFixed(1)} pp
    </span>
  );
}

function RelReturn({ v }: { v?: number | null }) {
  if (v === undefined || v === null) return <span style={{ color: "var(--text-muted)" }}>—</span>;
  return (
    <span style={{ color: v >= 0 ? "var(--success)" : "var(--error)" }}>
      {v >= 0 ? "+" : ""}{v.toFixed(2)}%
    </span>
  );
}

function WinRateCI({ d }: { d: Diagnostics }) {
  const [lo, hi] = d.win_rate_ci;
  const hasCI = lo !== null && hi !== null;
  const straddles = hasCI && lo <= 50 && hi >= 50;
  return (
    <>
      <span style={{ fontWeight: 600 }}>
        {d.win_rate !== null ? `${d.win_rate.toFixed(1)}%` : "—"}
      </span>
      {hasCI && (
        <>
          {" "}
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>({lo.toFixed(1)}–{hi.toFixed(1)})</span>
        </>
      )}
      {straddles && (
        <span
          title="95% CI includes 50% — not statistically distinguishable from a coin flip on this sample"
          style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: 4 }}
        >⊘</span>
      )}
    </>
  );
}

function SampleCell({ d }: { d: Diagnostics }) {
  return (
    <>
      <span style={{ fontWeight: 600 }}>{d.n_dates}</span>{" "}
      <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
        dates · {d.n_signals.toLocaleString()} signals
      </span>
    </>
  );
}

export default function TrackRecordDiagnostics({ data }: { data: DiagData }) {
  const [assetClass, setAssetClass] = useState<string>("all");

  // Which asset classes appear anywhere in the payload (drives the toggle).
  const available = useMemo(() => {
    const s = new Set<string>();
    const collect = (d?: Diagnostics) =>
      d?.by_asset_class && Object.keys(d.by_asset_class).forEach((k) => s.add(k));
    WINDOW_ORDER.forEach((k) => collect((data.windows[k] as { diagnostics?: Diagnostics } | undefined)?.diagnostics));
    collect(data.overall.diagnostics);
    return s;
  }, [data]);

  // Resolve the diagnostics to show for a base block given the selected class.
  const pick = (d?: Diagnostics): Diagnostics | undefined =>
    !d ? undefined : assetClass === "all" ? d : d.by_asset_class?.[assetClass];

  const rows = WINDOW_ORDER
    .map((k) => ({
      key: k,
      label: WINDOW_LABELS[k],
      // Record index type hides that a missing key is undefined at runtime — cast so the
      // optional chain is genuinely necessary (matches TrackRecord.tsx's own pattern).
      d: pick((data.windows[k] as { diagnostics?: Diagnostics } | undefined)?.diagnostics),
    }))
    .filter((r): r is { key: string; label: string; d: Diagnostics } => Boolean(r.d));
  const overall = pick(data.overall.diagnostics);
  if (rows.length === 0 && !overall) return null;
  const benchmark = data.overall.benchmark === "^GSPC" ? "S&P 500" : (data.overall.benchmark ?? "market");

  return (
    <Card>
      <CardBody>
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-1">
          <h5 className="mb-0" style={{ fontWeight: 700 }}>Measured against the right null</h5>
          <AssetClassToggle value={assetClass} onChange={setAssetClass} available={available} />
        </div>
        <p className="text-muted mb-3" style={{ fontSize: 14 }}>
          A win rate near 50% isn't automatically an edge — markets drift up, so the honest bar is the{" "}
          <strong>base rate</strong> (how often the stock rose anyway). And because same-day signals are
          correlated, the honest error bar clusters them, so the <strong>effective sample</strong> is the
          number of distinct dates, not the raw signal count. Here's the same performance held to that bar.
          {assetClass !== "all" && (
            <>
              {" "}Showing <strong>{assetClass}</strong> only, scored against the {assetClass} base rate —
              trending FX/crypto have base rates far from 50%, so equities held to the equity base rate is
              the honest read.
            </>
          )}
        </p>
        <div style={{ overflowX: "auto" }}>
          <Table borderless className="align-middle mb-0 tr-compact-table" style={{ fontSize: 14, minWidth: 640 }}>
            <thead>
              <tr style={{ color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.3 }}>
                <th>Window</th>
                <th>Win rate (95% CI)</th>
                <th>Effective sample</th>
                <th>Base rate ↑</th>
                <th>Bull edge</th>
                <th>Bear edge</th>
                <th>vs {benchmark}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ key, label, d }) => (
                <tr key={key} style={{ borderTop: "1px solid var(--border-color)" }}>
                  <td style={{ fontWeight: 600 }}>{label}</td>
                  <td><WinRateCI d={d} /></td>
                  <td><SampleCell d={d} /></td>
                  <td>{pct1(d.base_rate_up)}</td>
                  <td><EdgeCell v={d.bull_edge_over_base} /></td>
                  <td><EdgeCell v={d.bear_edge_over_base} /></td>
                  <td><RelReturn v={d.market_relative_avg_return} /></td>
                </tr>
              ))}
              {overall && (
                <tr style={{ borderTop: "2px solid var(--border-color)" }}>
                  <td style={{ fontWeight: 700 }}>Overall</td>
                  <td><WinRateCI d={overall} /></td>
                  <td><SampleCell d={overall} /></td>
                  <td style={{ color: "var(--text-muted)" }}>—</td>
                  <td style={{ color: "var(--text-muted)" }}>—</td>
                  <td style={{ color: "var(--text-muted)" }}>—</td>
                  <td><RelReturn v={overall.market_relative_avg_return} /></td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        <p className="text-muted mt-3 mb-0" style={{ fontSize: 12 }}>
          <strong>Bull/bear edge</strong> = win rate minus the base rate at that horizon (percentage points).{" "}
          <strong>⊘</strong> marks a 95% CI that includes 50% — not statistically distinguishable from chance on
          this sample. Confidence intervals are date-clustered bootstraps; <strong>vs {benchmark}</strong> is the
          average market-relative return per signal. Past performance does not guarantee future results. Not
          financial advice.
        </p>
      </CardBody>
    </Card>
  );
}
